import { useEffect, useRef } from 'react';
import Intercom, { update as updateIntercom, shutdown as shutdownIntercom } from '@intercom/messenger-js-sdk';
import * as Sentry from '@sentry/react';
import { resultIsError } from '@/services/HttpService';
import { useExerUser } from '../hooks/ExerUser';
import { useHttpContext } from '../hooks/HttpContext';
import { Maybe } from '../types';

interface IntercomInitType {
    app_id: string;
    intercom_user_jwt?: string;
    user_id?: string;
    email?: string;
    name?: string;
    phone?: string;
    created_at?: number;
    hide_default_launcher?: boolean;
}

export const IntercomWrapper = () => {
    const { user } = useExerUser();
    const { httpService } = useHttpContext();
    const isIntercomInitialized = useRef(false);
    const intercomUserId = useRef<Maybe<string>>();

    const bootOrUpdate = (props: IntercomInitType) => {
        if (!isIntercomInitialized.current) {
            Intercom(props);
            isIntercomInitialized.current = true;
        } else {
            updateIntercom(props);
        }
        intercomUserId.current = props.user_id;
    };

    const forceIntercomClear = async () => {
        shutdownIntercom();
        await new Promise((resolve) => setTimeout(resolve, 200));
        isIntercomInitialized.current = false;
        intercomUserId.current = undefined;
    };

    useEffect(() => {
        if (['STAGING', 'PRODUCTION', 'DEVELOPMENT'].includes(process.env.ENVIRONMENT!)) {
            const props: IntercomInitType = {
                app_id: process.env.INTERCOM_APP_ID!,
                user_id: undefined,
                hide_default_launcher: false,
            };

            (async () => {
                if (!user.isAuthenticated || intercomUserId.current !== user.id) {
                    forceIntercomClear();
                }

                if (user.isAuthenticated) {
                    props.user_id = user.id;
                    props.name = `${user.firstName} ${user.lastName}`;
                    props.email = user.email;
                    props.hide_default_launcher = true;

                    const res = await httpService.getIntercomToken();
                    if (resultIsError(res)) {
                        Sentry.captureException(`Intercom JWT unable to be fetched: ${res.message}`);
                    } else {
                        props.intercom_user_jwt = res.jwt;
                    }
                }
                bootOrUpdate(props);
            })();
        }
    }, [user.isAuthenticated, user.email, user.id, user.firstName, user.lastName, httpService]);

    return null;
};
